import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { createLead } from '@/api/clients'
import { reportGoogleAdsLeadConversion, trackOpenAiAdsLeadCreated } from '@/api/deferredAnalytics'
import { track } from '@/api/tracking'
import { useCookieConsent } from '@/hooks/useCookieConsent'
import { getAttributionParameters } from '@/lib/urlParams'
import { cn } from '@/lib/utils'
import { Sentry } from '@/sentry'
import { CreateLeadRequest, LandingPage, LeadCallIntent, UserType } from '@tipi/shared'

import { LeadFormOfferSuccess } from './LeadFormOfferSuccess'
import { MetaAdsAttributionConsentBlock } from './MetaAdsAttributionConsentBlock'
import { LeadFormConsentLegalNotice, PhoneCommercialConsentBlock } from './PhoneCommercialConsentBlock'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Select } from './ui/select'

export type LeadFormSuccessResult = {
    leadId: string
    createdAt: string
}

// Common country codes for phone numbers
const countryCodes = [
    { code: '+34', country: 'ES' },
    { code: '+1', country: 'US/CA' },
    { code: '+44', country: 'UK' },
    { code: '+33', country: 'FR' },
    { code: '+49', country: 'DE' },
    { code: '+39', country: 'IT' },
    { code: '+351', country: 'PT' },
    { code: '+52', country: 'MX' },
    { code: '+55', country: 'BR' },
    { code: '+91', country: 'IN' },
]

// Special value for "Other" option
const OTHER_PREFIX = 'other'

const USER_TYPE_OPTIONS: { value: UserType; label: string }[] = [
    { value: 'cook_and_manager', label: 'Cocino y soy el dueño' },
    { value: 'dining_room_and_manager', label: 'Estoy en sala y soy el dueño' },
    { value: 'head_chef', label: 'Jefe de cocina' },
    { value: 'businessman', label: 'Empresario' },
    { value: 'executive_chef', label: 'Chef ejecutivo' },
    { value: 'gastronomic_advisor', label: 'Asesor gastronómico' },
    { value: 'home_cook', label: 'Cocino en casa' },
    { value: 'other', label: 'Otro' },
]

const FORM_FIELD_CLASSNAME = 'h-9 px-2 text-xs'

interface LeadFormProps {
    landingPage: LandingPage
    successMessage?: string
    successMessageClassName?: string
    /** Runs right after the lead API succeeds (before success UI settles). Parents may navigate away/unmount immediately, so callers should not rely on the default success panel showing. */
    onSuccess?: (result: LeadFormSuccessResult) => void
    /** When true (default), show the 24h intro-offer card after submit. Set false to keep the plain success message. */
    showIntroOfferOnSuccess?: boolean
    successActionText?: string
    onSuccessAction?: () => void
    mixpanelContext?: Record<string, unknown>
    /** Optional free-text concern / offer path persisted on the lead (Notion + Slack). */
    mainConcern?: string
    defaultCallIntent?: LeadCallIntent
    ctaTrustText?: string
    submitButtonRef?: React.RefObject<HTMLButtonElement | null>
    submitButtonText?: string
    submitButtonIcon?: React.ReactNode
    alternateSubmitButtonText?: string
    /** Larger CTA and slightly de-emphasised consent so the submit action reads as the main step. */
    emphasizePrimaryCta?: boolean
    fieldClassName?: string
    consentSectionClassName?: string
    consentLabelClassName?: string
    consentSubtextClassName?: string
    submitButtonClassName?: string
    alternateSubmitButtonClassName?: string
    formErrorClassName?: string
    nameFieldLabel?: string
    phoneFieldLabel?: string
    roleFieldLabel?: string
    fieldLabelClassName?: string
}

export function LeadForm({
    landingPage,
    successMessage,
    successMessageClassName,
    onSuccess,
    showIntroOfferOnSuccess = true,
    successActionText,
    onSuccessAction,
    mixpanelContext,
    mainConcern,
    defaultCallIntent,
    ctaTrustText,
    submitButtonRef,
    submitButtonText,
    submitButtonIcon,
    alternateSubmitButtonText,
    emphasizePrimaryCta = false,
    fieldClassName,
    consentSectionClassName,
    consentLabelClassName,
    consentSubtextClassName,
    submitButtonClassName,
    alternateSubmitButtonClassName,
    formErrorClassName,
    nameFieldLabel,
    phoneFieldLabel,
    roleFieldLabel,
    fieldLabelClassName,
}: LeadFormProps) {
    const { t } = useTranslation()
    const { preferences } = useCookieConsent()
    const hasBannerMarketingConsent = preferences.marketing
    const inputClassName = fieldClassName ?? FORM_FIELD_CLASSNAME
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [formSuccess, setFormSuccess] = useState(false)
    const [offerSuccess, setOfferSuccess] = useState<LeadFormSuccessResult | null>(null)
    const [formError, setFormError] = useState<string | null>(null)
    const [phoneError, setPhoneError] = useState<string | null>(null)
    const [hasInteracted, setHasInteracted] = useState(false)
    const [consentChecked, setConsentChecked] = useState(false)
    const [metaAdsConsentChecked, setMetaAdsConsentChecked] = useState(false)
    const [formData, setFormData] = useState({
        name: '',
        phonePrefix: '+34', // Default to Spain
        customPhonePrefix: '',
        phone: '',
        userType: '' as UserType | '',
    })

    const validatePhone = (
        phone: string,
        prefix: string,
    ): { isValid: boolean; errorType?: 'invalidChars' | 'invalidLength' } => {
        if (!phone) return { isValid: false, errorType: 'invalidLength' }

        // Check if phone contains only digits, spaces, and hyphens
        const validCharsRegex = /^[\d\s-]+$/
        if (!validCharsRegex.test(phone)) {
            return { isValid: false, errorType: 'invalidChars' }
        }

        // Remove spaces and hyphens to count digits
        const cleanedPhone = phone.replace(/[\s-]/g, '')

        // For Spain (+34), require exactly 9 digits and start with 6 or 7 (mobile numbers)
        if (prefix === '+34') {
            const isValidLength = cleanedPhone.length === 9
            const startsWithMobilePrefix = cleanedPhone.startsWith('6') || cleanedPhone.startsWith('7')

            return isValidLength && startsWithMobilePrefix
                ? { isValid: true }
                : { isValid: false, errorType: 'invalidLength' }
        }

        // For other countries, require at least 6 digits (basic validation)
        return cleanedPhone.length >= 6 ? { isValid: true } : { isValid: false, errorType: 'invalidLength' }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target

        // Track form started event on first interaction
        if (!hasInteracted) {
            setHasInteracted(true)
            track('Form started', { landing_page: landingPage, ...mixpanelContext })
        }

        // Clear phone error when user starts typing
        if (name === 'phone' && phoneError) {
            setPhoneError(null)
        }

        if (name === 'phonePrefix' && value !== OTHER_PREFIX) {
            // When selecting a preset country code, clear any custom prefix
            setFormData(prev => ({
                ...prev,
                phonePrefix: value,
                customPhonePrefix: '',
            }))
            // Clear phone error when changing prefix
            if (phoneError) {
                setPhoneError(null)
            }
        } else if (name === 'customPhonePrefix') {
            // When entering a custom prefix, make sure it always starts with +
            const normalizedValue = value.startsWith('+') ? value : `+${value}`
            setFormData(prev => ({
                ...prev,
                customPhonePrefix: normalizedValue,
            }))
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value,
            }))
        }
    }

    // Determine the actual phone prefix to use
    const actualPhonePrefix = formData.phonePrefix === OTHER_PREFIX ? formData.customPhonePrefix : formData.phonePrefix

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setIsSubmitting(true)
        setFormError(null)
        setPhoneError(null)

        const submittedCallIntent = alternateSubmitButtonText
            ? new FormData(e.currentTarget).get('callIntent')
            : (defaultCallIntent ?? null)
        const callIntent: LeadCallIntent | undefined =
            submittedCallIntent === 'today' || submittedCallIntent === 'schedule' ? submittedCallIntent : undefined

        // Validate phone number
        const phoneValidation = validatePhone(formData.phone, actualPhonePrefix)
        if (!phoneValidation.isValid) {
            const errorMessage =
                phoneValidation.errorType === 'invalidChars'
                    ? t('leadForm.phoneInvalidChars')
                    : t('leadForm.phoneInvalid')
            setPhoneError(errorMessage)
            setIsSubmitting(false)
            return
        }

        // Validate user type selection
        if (!formData.userType) {
            setFormError(t('leadForm.userTypeRequired'))
            setIsSubmitting(false)
            return
        }

        // Validate consent checkbox
        if (!consentChecked) {
            setFormError(t('leadForm.consentRequired'))
            setIsSubmitting(false)
            track('Form checking call consent', { landing_page: landingPage, ...mixpanelContext })
            return
        }

        try {
            const leadData: CreateLeadRequest = {
                name: formData.name,
                phone: `${actualPhonePrefix} ${formData.phone}`,
                userType: formData.userType,
                landing_page: landingPage,
                callIntent,
                ...(mainConcern ? { mainConcern } : {}),
                metaAdsAttributionConsent: hasBannerMarketingConsent || metaAdsConsentChecked,
                ...getAttributionParameters(),
            }

            const createLeadResult = await createLead(leadData)
            const successResult: LeadFormSuccessResult = {
                leadId: createLeadResult.leadId,
                createdAt: createLeadResult.createdAt,
            }

            setFormSuccess(true)
            if (showIntroOfferOnSuccess && successResult.createdAt) {
                setOfferSuccess(successResult)
            }
            // Fires before React paints `formSuccess` UI — fine when parent uses onSuccess to navigate and unmounts this form first.
            onSuccess?.(successResult)
            setFormData({
                name: '',
                phonePrefix: '+34',
                customPhonePrefix: '',
                phone: '',
                userType: '',
            })
            setPhoneError(null)

            const leadDataForMixpanel = {
                userType: leadData.userType,
                landing_page: leadData.landing_page,
                utm_source: leadData.utm_source,
                utm_medium: leadData.utm_medium,
                utm_campaign: leadData.utm_campaign,
                utm_term: leadData.utm_term,
                utm_content: leadData.utm_content,
                fbclid: leadData.fbclid,
                gclid: leadData.gclid,
                gad_source: leadData.gad_source,
                gad_campaignid: leadData.gad_campaignid,
                gbraid: leadData.gbraid,
                referring_domain: leadData.referring_domain,
                initial_landing_path: leadData.initial_landing_path,
                acquisition_channel: leadData.acquisition_channel,
            }
            track(
                'lead_form_success',
                {
                    ...leadDataForMixpanel,
                    call_intent: callIntent,
                    landing_page: landingPage,
                    conversion_path: 'lead_form',
                    ...mixpanelContext,
                },
                { send_immediately: true },
            )

            reportGoogleAdsLeadConversion({ userType: formData.userType })
            trackOpenAiAdsLeadCreated()
        } catch (error) {
            console.error('Error submitting form:', error)
            setFormError(t('leadForm.errorMessage'))
            Sentry.captureException(error, {
                tags: { source: 'lead_form' },
                extra: { landing_page: landingPage, ...mixpanelContext },
            })
            track(
                'lead_form_error',
                {
                    error: error,
                    landing_page: landingPage,
                    ...mixpanelContext,
                },
                { send_immediately: true },
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    if (formSuccess && offerSuccess) {
        return (
            <LeadFormOfferSuccess
                leadId={offerSuccess.leadId}
                createdAt={offerSuccess.createdAt}
                landingPage={landingPage}
                mixpanelContext={mixpanelContext}
            />
        )
    }

    if (formSuccess) {
        return (
            <div className='rounded-lg p-6 text-center'>
                <p className={cn('text-green-500', successMessageClassName)}>
                    {successMessage ?? t('leadForm.successMessage')}
                </p>
                {successActionText && onSuccessAction && (
                    <Button type='button' variant='outline' className='mt-4 w-full' onClick={onSuccessAction}>
                        {successActionText}
                    </Button>
                )}
            </div>
        )
    }

    return (
        <form onSubmit={handleSubmit} className='space-y-3'>
            <div className='space-y-2'>
                {nameFieldLabel ? (
                    <label
                        htmlFor='name'
                        className={cn(
                            'text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground',
                            fieldLabelClassName,
                        )}
                    >
                        {nameFieldLabel}
                    </label>
                ) : null}
                <Input
                    id='name'
                    name='name'
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('leadForm.namePlaceholder')}
                    className={inputClassName}
                    required
                />
            </div>

            <div className='space-y-2'>
                {phoneFieldLabel ? (
                    <label
                        htmlFor='phonePrefix'
                        className={cn(
                            'text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground',
                            fieldLabelClassName,
                        )}
                    >
                        {phoneFieldLabel}
                    </label>
                ) : null}
                <div className='space-y-2'>
                    <div
                        className={cn(
                            'overflow-hidden rounded-[14px]',
                            formData.phonePrefix !== OTHER_PREFIX &&
                                'grid grid-cols-[6.5rem_minmax(0,1fr)] items-stretch',
                        )}
                    >
                        <Select
                            id='phonePrefix'
                            name='phonePrefix'
                            value={formData.phonePrefix}
                            onChange={handleChange}
                            className={cn(
                                inputClassName,
                                formData.phonePrefix !== OTHER_PREFIX && 'rounded-r-none border-r-0',
                            )}
                        >
                            {countryCodes.map(({ code, country }) => (
                                <option key={code} value={code}>
                                    {code} {country}
                                </option>
                            ))}
                            <option value={OTHER_PREFIX}>{t('leadForm.otherPrefix')}</option>
                        </Select>
                        {formData.phonePrefix !== OTHER_PREFIX && (
                            <Input
                                id='phone'
                                name='phone'
                                type='tel'
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder={t('leadForm.phonePlaceholder')}
                                className={cn(inputClassName, 'rounded-l-none border-l')}
                                required
                            />
                        )}
                    </div>

                    {formData.phonePrefix === OTHER_PREFIX && (
                        <div className='grid grid-cols-[6.5rem_minmax(0,1fr)] items-stretch overflow-hidden rounded-[14px]'>
                            <Input
                                id='customPhonePrefix'
                                name='customPhonePrefix'
                                type='text'
                                value={formData.customPhonePrefix}
                                onChange={handleChange}
                                placeholder={t('leadForm.customPrefixPlaceholder')}
                                className={cn(inputClassName, 'rounded-r-none border-r-0')}
                                required={formData.phonePrefix === OTHER_PREFIX}
                            />
                            <Input
                                id='phone'
                                name='phone'
                                type='tel'
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder={t('leadForm.phonePlaceholder')}
                                className={cn(inputClassName, 'rounded-l-none border-l')}
                                required
                            />
                        </div>
                    )}
                    {phoneError && (
                        <div className={cn('text-sm text-destructive', formErrorClassName)}>{phoneError}</div>
                    )}
                </div>
            </div>

            <div className='space-y-2'>
                {roleFieldLabel ? (
                    <label
                        htmlFor='userType'
                        className={cn(
                            'text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground',
                            fieldLabelClassName,
                        )}
                    >
                        {roleFieldLabel}
                    </label>
                ) : null}
                <Select
                    id='userType'
                    name='userType'
                    value={formData.userType}
                    onChange={handleChange}
                    className={inputClassName}
                    required
                >
                    <option value='' disabled>
                        {t('leadForm.userTypePlaceholder')}
                    </option>
                    {USER_TYPE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </Select>
            </div>

            {/*
            <div className='space-y-2'>
                {isFreeTextMainConcernEnabled ? (
                    <Input
                        id='mainConcern'
                        name='mainConcern'
                        value={formData.mainConcern}
                        onChange={handleChange}
                        minLength={10}
                        placeholder={t(
                            'leadForm.mainConcern.placeholder',
                            'What problem worries you the most right now?',
                        )}
                        required
                    />
                ) : (
                    <Select
                        id='mainConcern'
                        name='mainConcern'
                        value={formData.mainConcern}
                        onChange={handleChange}
                        required
                    >
                        <option value='' disabled>
                            {t('leadForm.mainConcern.placeholder', 'What problem worries you the most right now?')}
                        </option>
                        {randomizedMainConcernOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {t(option.labelKey, option.fallbackLabel)}
                            </option>
                        ))}
                    </Select>
                )}
            </div>

            <div className='space-y-2'>
                <Select
                    id='resolutionTimeframe'
                    name='resolutionTimeframe'
                    value={formData.resolutionTimeframe}
                    onChange={handleChange}
                    required
                >
                    <option value='' disabled>
                        {t(
                            'leadForm.resolutionTimeframe.placeholder',
                            'In how much time would you like to solve this problem?',
                        )}
                    </option>
                    {RESOLUTION_TIMEFRAME_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                            {t(option.labelKey, option.fallbackLabel)}
                        </option>
                    ))}
                </Select>
            </div>

            <div className='space-y-2'>
                <Input
                    id='numbersReviewFrequency'
                    name='numbersReviewFrequency'
                    value={formData.numbersReviewFrequency}
                    onChange={handleChange}
                    placeholder={t(
                        'leadForm.numbersReviewFrequency.placeholder',
                        'How often do you review the numbers of your business?',
                    )}
                    required
                />
            </div>
            */}

            <div
                className={cn(
                    'space-y-3',
                    emphasizePrimaryCta &&
                        'mt-1 border-t border-border/50 pt-4 opacity-[0.88] [&_label]:text-xs [&_p]:text-[11px]',
                    consentSectionClassName,
                )}
            >
                <PhoneCommercialConsentBlock
                    consentChecked={consentChecked}
                    onCheckedChange={(checked: boolean) => {
                        setConsentChecked(checked)
                        if (!hasInteracted) {
                            setHasInteracted(true)
                            track('Form started', { landing_page: landingPage, ...mixpanelContext })
                        }
                    }}
                    labelClassName={consentLabelClassName}
                />
                {hasBannerMarketingConsent ? null : (
                    <MetaAdsAttributionConsentBlock
                        consentChecked={metaAdsConsentChecked}
                        onCheckedChange={(checked: boolean) => {
                            setMetaAdsConsentChecked(checked)
                            if (!hasInteracted) {
                                setHasInteracted(true)
                                track('Form started', { landing_page: landingPage, ...mixpanelContext })
                            }
                        }}
                        labelClassName={consentLabelClassName}
                    />
                )}
                <LeadFormConsentLegalNotice className={consentSubtextClassName} />
            </div>

            {formError && <div className={cn('text-sm text-destructive', formErrorClassName)}>{formError}</div>}

            <div className={cn(alternateSubmitButtonText && 'space-y-3')}>
                <Button
                    ref={submitButtonRef}
                    type='submit'
                    {...(alternateSubmitButtonText ? { name: 'callIntent', value: 'today' } : {})}
                    variant='default'
                    size={emphasizePrimaryCta ? 'lg' : 'default'}
                    className={cn(
                        'w-full',
                        emphasizePrimaryCta && 'text-base font-semibold shadow-sm',
                        submitButtonClassName,
                    )}
                    disabled={isSubmitting || !consentChecked}
                >
                    {isSubmitting ? (
                        t('leadForm.submitting')
                    ) : (
                        <>
                            {submitButtonIcon}
                            {submitButtonText ?? t('leadForm.submit')}
                        </>
                    )}
                </Button>

                {alternateSubmitButtonText ? (
                    <Button
                        type='submit'
                        name='callIntent'
                        value='schedule'
                        variant='outline'
                        size={emphasizePrimaryCta ? 'lg' : 'default'}
                        className={cn(
                            'w-full',
                            emphasizePrimaryCta && 'text-sm font-semibold',
                            alternateSubmitButtonClassName,
                        )}
                        disabled={isSubmitting || !consentChecked}
                    >
                        {isSubmitting ? t('leadForm.submitting') : alternateSubmitButtonText}
                    </Button>
                ) : null}
            </div>

            {ctaTrustText && <p className='text-center text-xs text-muted-foreground'>{ctaTrustText}</p>}
        </form>
    )
}
